import {
  ActionField,
  ActionFieldChildProps,
  useFormContext,
  withForm,
} from '@oscar/form';
import { Col, Row } from '@oscar/theme';
import { useDeepCompareEffect } from '@oscar/utils';
import {
  MessageSuccess,
  PageHeader,
  StatusTagColumnTable,
  Loading,
} from 'common/components';
import { FORMAT_DATE, MESSAGE_NOTICE, REQUEST_STATUS } from 'common/constants';
import { APIS } from 'common/constants/endpoints';
import {
  INSULATION_HEADERS,
  LIST_KEY_CREATE_REQUEST,
} from 'common/constants/insulation';
import { useAuth } from 'features/auth';

import {
  ActivityLog,
  FinalHandOver,
  MAP_REQUEST_NAME,
  PaintingButtons,
  PaintingDetail,
  ProposalDetail,
  RequestDataType,
  RequestorDetail,
  ScaffoldDetail,
  WorkDetail,
  WorkFlow,
} from 'features/insulation';
import PaintingExecution from 'features/insulation/components/QaqcDetail/component/PaintingExecution';
import SurfacePreparation from 'features/insulation/components/QaqcDetail/component/SurfacePreparation';
import moment from 'moment';
import React, { useMemo, useState } from 'react';
import VisibleView from './components/VisibleView';
import {
  validateExecutionPendingPainting,
  validateExecutionQaqc,
  validateProposalPendingPainting,
  validateSurfacePendingPainting,
  validateSurfaceQaqc,
} from './ConfigPainting';
import useStatus from './hooks/useStatus';
import './style.scss';
import { getSuffixName, getValueByStatus, validateData } from './utils';

const PAINTING_WORK: RequestDataType = {
  PENDING_PAINTING: [
    {
      key: LIST_KEY_CREATE_REQUEST.PROPOSAL_DETAIL,
      header: INSULATION_HEADERS.PROPOSAL_DETAIL,
      component: ProposalDetail,
      roles: [['PENDING_PAINTING', 'SUBMITTED']],
      validate: validateProposalPendingPainting,
    },
    {
      key: LIST_KEY_CREATE_REQUEST.SURFACE_PREPARATION_DETAIL,
      header: INSULATION_HEADERS.SURFACE_PREPARATION,
      component: SurfacePreparation,
      roles: [['PENDING_PAINTING', 'SUBMITTED']],
      validate: validateSurfacePendingPainting,
    },
    {
      key: LIST_KEY_CREATE_REQUEST.PAINTING_EXECUTION_DETAIL,
      header: INSULATION_HEADERS.PAINTING_EXECUTION,
      component: PaintingExecution,
      roles: [['PENDING_PAINTING', 'SUBMITTED']],
      validate: validateExecutionPendingPainting,
    },
  ],
  PENDING_PAINTING_1: [
    {
      key: LIST_KEY_CREATE_REQUEST.SURFACE_PREPARATION_DETAIL,
      header: INSULATION_HEADERS.SURFACE_PREPARATION,
      component: SurfacePreparation,
      roles: [['PENDING_PAINTING_1', 'SUBMITTED']],
      validate: validateSurfacePendingPainting,
    },
    {
      key: LIST_KEY_CREATE_REQUEST.PAINTING_EXECUTION_DETAIL,
      header: INSULATION_HEADERS.PAINTING_EXECUTION,
      component: PaintingExecution,
      roles: [['PENDING_PAINTING_1', 'SUBMITTED']],
      validate: validateExecutionPendingPainting,
    },
  ],
  PENDING_PAINTING_2: [
    {
      key: LIST_KEY_CREATE_REQUEST.PAINTING_EXECUTION_DETAIL,
      header: INSULATION_HEADERS.PAINTING_EXECUTION,
      component: PaintingExecution,
      roles: [['PENDING_PAINTING_2', 'SUBMITTED']],
      validate: validateExecutionPendingPainting,
    },
  ],
  PENDING_PAINTING_3: [
    {
      key: LIST_KEY_CREATE_REQUEST.PAINTING_EXECUTION_DETAIL,
      header: INSULATION_HEADERS.PAINTING_EXECUTION,
      component: PaintingExecution,
      roles: [['PENDING_PAINTING_3', 'SUBMITTED']],
      validate: validateExecutionPendingPainting,
    },
  ],
  PENDING_PAINTING_4: [
    {
      key: LIST_KEY_CREATE_REQUEST.PAINTING_EXECUTION_DETAIL,
      header: INSULATION_HEADERS.PAINTING_EXECUTION,
      component: PaintingExecution,
      roles: [['PENDING_PAINTING_4', 'SUBMITTED']],
      validate: validateExecutionPendingPainting,
    },
  ],
  PENDING_PAINTING_5: [
    {
      key: LIST_KEY_CREATE_REQUEST.PROPOSAL_DETAIL,
      header: INSULATION_HEADERS.PROPOSAL_DETAIL,
      component: ProposalDetail,
      roles: [
        ['PENDING_PAINTING_2', 'SUBMITTED'],
        ['PENDING_PAINTING_3', 'SUBMITTED'],
        ['PENDING_PAINTING_4', 'SUBMITTED'],
        ['PENDING_PAINTING_5', 'SUBMITTED'],
      ],
      validate: validateProposalPendingPainting,
    },
    {
      key: LIST_KEY_CREATE_REQUEST.PAINTING_EXECUTION_DETAIL,
      header: INSULATION_HEADERS.PAINTING_EXECUTION,
      component: PaintingExecution,
      roles: [
        ['PENDING_PAINTING_2', 'SUBMITTED'],
        ['PENDING_PAINTING_3', 'SUBMITTED'],
        ['PENDING_PAINTING_4', 'SUBMITTED'],
        ['PENDING_PAINTING_5', 'SUBMITTED'],
      ],
      validate: validateExecutionPendingPainting,
    },
  ],
  PENDING_QAQC: [
    {
      key: LIST_KEY_CREATE_REQUEST.SURFACE_PREPARATION_DETAIL,
      header: INSULATION_HEADERS.SURFACE_PREPARATION,
      component: SurfacePreparation,
      roles: [['PENDING_QAQC', 'SUBMITTED']],
      validate: validateSurfaceQaqc,
    },
    {
      key: LIST_KEY_CREATE_REQUEST.PAINTING_EXECUTION_DETAIL,
      header: INSULATION_HEADERS.PAINTING_EXECUTION,
      component: PaintingExecution,
      roles: [['PENDING_QAQC', 'SUBMITTED']],
      validate: validateExecutionQaqc,
    },
  ],
  PENDING_QAQC_1: [
    {
      key: LIST_KEY_CREATE_REQUEST.SURFACE_PREPARATION_DETAIL,
      header: INSULATION_HEADERS.SURFACE_PREPARATION,
      component: SurfacePreparation,
      roles: [['PENDING_QAQC_1', 'SUBMITTED']],
      validate: validateSurfaceQaqc,
    },
    {
      key: LIST_KEY_CREATE_REQUEST.PAINTING_EXECUTION_DETAIL,
      header: INSULATION_HEADERS.PAINTING_EXECUTION,
      component: PaintingExecution,
      roles: [['PENDING_QAQC_1', 'SUBMITTED']],
      validate: validateExecutionQaqc,
    },
  ],
  PENDING_QAQC_2: [
    {
      key: LIST_KEY_CREATE_REQUEST.PAINTING_EXECUTION_DETAIL,
      header: INSULATION_HEADERS.PAINTING_EXECUTION,
      component: PaintingExecution,
      roles: [['PENDING_QAQC_2', 'SUBMITTED']],
      validate: validateExecutionQaqc,
    },
  ],
  PENDING_QAQC_3: [
    {
      key: LIST_KEY_CREATE_REQUEST.PAINTING_EXECUTION_DETAIL,
      header: INSULATION_HEADERS.PAINTING_EXECUTION,
      component: PaintingExecution,
      roles: [['PENDING_QAQC_3', 'SUBMITTED']],
      validate: validateExecutionQaqc,
    },
  ],
  PENDING_QAQC_4: [
    {
      key: LIST_KEY_CREATE_REQUEST.PAINTING_EXECUTION_DETAIL,
      header: INSULATION_HEADERS.PAINTING_EXECUTION,
      component: PaintingExecution,
      roles: [['PENDING_QAQC_4', 'SUBMITTED']],
      validate: validateExecutionQaqc,
    },
  ],
  PENDING_QAQC_5: [
    {
      key: LIST_KEY_CREATE_REQUEST.PAINTING_EXECUTION_DETAIL,
      header: INSULATION_HEADERS.PAINTING_EXECUTION,
      component: PaintingExecution,
      roles: [
        ['PENDING_QAQC_2', 'SUBMITTED'],
        ['PENDING_QAQC_3', 'SUBMITTED'],
        ['PENDING_QAQC_4', 'SUBMITTED'],
        ['PENDING_QAQC_5', 'SUBMITTED'],
      ],
      validate: validateExecutionQaqc,
    },
  ],
};

const PAINTING_PANELS: RequestDataType = {
  DRAFT: [
    {
      key: LIST_KEY_CREATE_REQUEST.REQUESTOR_DETAIL,
      header: INSULATION_HEADERS.REQUESTOR_DETAIL,
      component: RequestorDetail,
      roles: [['DRAFT', 'SUBMITTED']],
      validate: ['section', 'discipline'],
    },
    {
      key: LIST_KEY_CREATE_REQUEST.WORK_DETAIL,
      header: INSULATION_HEADERS.WORK_DETAIL,
      component: WorkDetail,
      roles: [['DRAFT', 'SUBMITTED']],
      validate: [(data: any) => data?.isWorkDetailRequire && 'listWorkDetail'],
    },
    {
      key: LIST_KEY_CREATE_REQUEST.SCAFFOLD_DETAIL,
      header: INSULATION_HEADERS.SCAFFOLD_DETAIL,
      component: ScaffoldDetail,
      roles: [['DRAFT', 'SUBMITTED']],
      validate: [
        (data: any) => data?.isScaffoldingRequire && 'listScaffoldDetail',
      ],
    },
    {
      key: LIST_KEY_CREATE_REQUEST.PAINTING_DETAIL,
      header: INSULATION_HEADERS.PAINTING_DETAIL,
      component: PaintingDetail,
      roles: [['DRAFT', 'EDITED']],
      validate: [
        'workDescription',
        'coatingSystem',
        'serviceCategory',
        'environment',
        'siteVisitDate',
        'area',
        'endDate',
        'startDate',
      ],
    },
  ],
  PENDING_VALIDTION: [
    {
      key: LIST_KEY_CREATE_REQUEST.REQUESTOR_DETAIL,
      header: INSULATION_HEADERS.REQUESTOR_DETAIL,
      component: RequestorDetail,
      roles: [
        ['PENDING_VALIDTION', 'EDITED'],
        ['PENDING_VALIDTION', 'SUBMITTED', 'PAINTING'],
      ],
      validate: ['section', 'discipline'],
    },
    {
      key: LIST_KEY_CREATE_REQUEST.WORK_DETAIL,
      header: INSULATION_HEADERS.WORK_DETAIL,
      component: WorkDetail,
      roles: [
        ['PENDING_VALIDTION', 'EDITED'],
        ['PENDING_VALIDTION', 'SUBMITTED', 'PAINTING'],
      ],
      validate: [(data: any) => data?.isWorkDetailRequire && 'listWorkDetail'],
    },
    {
      key: LIST_KEY_CREATE_REQUEST.SCAFFOLD_DETAIL,
      header: INSULATION_HEADERS.SCAFFOLD_DETAIL,
      component: ScaffoldDetail,
      roles: [
        ['PENDING_VALIDTION', 'EDITED'],
        ['PENDING_VALIDTION', 'SUBMITTED', 'PAINTING'],
      ],
      validate: [
        (data: any) => data?.isScaffoldingRequire && 'listScaffoldDetail',
      ],
    },
    {
      key: LIST_KEY_CREATE_REQUEST.PAINTING_DETAIL,
      header: INSULATION_HEADERS.PAINTING_DETAIL,
      component: PaintingDetail,
      roles: [
        ['PENDING_VALIDTION', 'EDITED'],
        ['PENDING_VALIDTION', 'SUBMITTED', 'PAINTING'],
      ],
      validate: [
        'workDescription',
        'coatingSystem',
        'serviceCategory',
        'environment',
        'siteVisitDate',
        'area',
        'endDate',
        'startDate',
        data => {
          if (data?.systemId === 69) {
            return 'otherCoatsList';
          }
          return undefined;
        },
        (_data, helpers) => {
          if (
            helpers?.checkRole?.('PENDING_VALIDTION', 'SUBMITTED', 'PAINTING')
          ) {
            return 'workAssigned';
          }
        },
        (_data, helpers) => {
          if (
            helpers?.checkRole?.('PENDING_VALIDTION', 'SUBMITTED', 'PAINTING')
          ) {
            return 'mlngciAssignedName';
          }
        },
        (_data, helpers) => {
          if (
            helpers?.checkRole?.('PENDING_VALIDTION', 'SUBMITTED', 'PAINTING')
          ) {
            return 'systemId';
          }
        },
      ],
    },
  ],
  PENDING_PROPOSAL: [
    {
      key: LIST_KEY_CREATE_REQUEST.PROPOSAL_DETAIL,
      header: INSULATION_HEADERS.PROPOSAL_DETAIL,
      component: ProposalDetail,
      roles: [['PENDING_PROPOSAL', 'SUBMITTED']],
      validate: ['proposalBillingDetail.data'],
      isVisibleByRole: true,
    },
  ],
  PENDING_PAINTING: PAINTING_WORK.PENDING_PAINTING,
  PENDING_QAQC: PAINTING_WORK.PENDING_QAQC,
  PENDING_PAINTING_1: PAINTING_WORK.PENDING_PAINTING_1,
  PENDING_QAQC_1: PAINTING_WORK.PENDING_QAQC_1,
  PENDING_PAINTING_2: PAINTING_WORK.PENDING_PAINTING_2,
  PENDING_QAQC_2: PAINTING_WORK.PENDING_QAQC_2,
  PENDING_PAINTING_3: PAINTING_WORK.PENDING_PAINTING_3,
  PENDING_QAQC_3: PAINTING_WORK.PENDING_QAQC_3,
  PENDING_PAINTING_4: PAINTING_WORK.PENDING_PAINTING_4,
  PENDING_QAQC_4: PAINTING_WORK.PENDING_QAQC_4,
  PENDING_PAINTING_5: PAINTING_WORK.PENDING_PAINTING_5,
  PENDING_QAQC_5: PAINTING_WORK.PENDING_QAQC_5,
  BILLING: [
    {
      key: LIST_KEY_CREATE_REQUEST.PROPOSAL_DETAIL,
      header: INSULATION_HEADERS.PROPOSAL_DETAIL,
      component: ProposalDetail,
      roles: [['BILLING', 'SUBMITTED', 'PAINTING']],
      validate: [
        'proposalBillingDetail.poNumber',
        'proposalBillingDetail.verifiedFinalCost',
      ],
    },
    {
      key: LIST_KEY_CREATE_REQUEST.FINAL_HAND_OVER,
      header: INSULATION_HEADERS.FINAL_HAND_OVER,
      component: FinalHandOver,
      roles: [['BILLING', 'SUBMITTED', 'PAINTING']],
      validate: [
        'proposalBillingDetail.executiveInCharge',
        'proposalBillingDetail.date',
        'proposalBillingDetail.poNumber',
        'proposalBillingDetail.verifiedFinalCost',
      ],
    },
  ],
  CLOSED: [],
};

export const Painting = withForm({
  name: 'painting',
  preload: [
    {
      name: 'metaDataRequestorDetail',
      url: APIS.META_DATA_REQUESTOR_DETAIL,
      method: 'GET',
    },
    {
      name: 'priorities',
      url: APIS.META_DATA_PRIORITY,
      method: 'GET',
    },
    {
      name: 'metaDataPaintingValidationDetail',
      url: APIS.META_DATA_VALIDATION_PAINTING,
      method: 'GET',
    },
    {
      name: 'metaDataProposalDetail',
      url: APIS.META_DATA_PROPOSAL_DETAIL,
      method: 'GET',
    },
    {
      name: 'getPaintingWorkDetail',
      url: APIS.GET_PAINTING_WORK_DETAIL,
      method: 'GET',
    },
    {
      name: 'metaDataInsulationDetail',
      url: APIS.META_DATA_INSULATION_DETAIL,
      method: 'GET',
    },
  ],
  initialValues: {
    isWorkDetailRequire: true,
    isScaffoldingRequire: false,
    siteVisitDate: moment(
      `${moment().format(FORMAT_DATE.SHORT_MONTH_DATE)} 08:00 AM`,
    ),
  },
  request: {
    name: 'fetchRequestByID',
    method: 'GET',
    nextUrl: (props: any) => {
      return `${APIS.PAINTING_GET_REQUEST_BY_CUSTOM_ID}?requestId=${props?.params?.id}`;
    },
    shouldRequest: (props: any) => {
      return !!props?.params?.id;
    },
  },
  size: 'large',
})((props: any) => {
  const [formContext] = useFormContext();
  const [statusRequest] = useStatus();
  const [auth] = useAuth();
  const [isSubmitSuccess, setIsSubmitSuccess] = useState({
    code: 0,
    message: '',
    data: 0,
  });
  const [formStatus, setFormStatus] = useState({});
  const { submitQAQCsTime, lastSubmittedStatus } =
    formContext.selectors?.getInitalValue() || {};
  useDeepCompareEffect(() => {
    formContext.form?.reset?.();
  }, [statusRequest?.state, statusRequest?.id]);

  const paintingConfiguration = useMemo(() => {
    const result: RequestDataType = {};
    const { needToSubmittedTime } = submitQAQCsTime || {};

    for (const key in PAINTING_PANELS) {
      if (needToSubmittedTime > 1) {
        if (key.includes('PENDING_PAINTING') || key.includes('PENDING_QAQC')) {
          const stage = key.match(/[\d]+/g)?.[0];
          if (stage && parseInt(stage) < needToSubmittedTime) {
            result[key] = PAINTING_PANELS[key];
          } else if (stage && parseInt(stage) === needToSubmittedTime) {
            result[key] = key.includes('PENDING_PAINTING')
              ? PAINTING_PANELS['PENDING_PAINTING_5']
              : PAINTING_PANELS['PENDING_QAQC_5'];
          }
        } else {
          result[key] = PAINTING_PANELS[key];
        }
      } else if (
        !key.includes('PENDING_PAINTING_') &&
        !key.includes('PENDING_QAQC_')
      ) {
        result[key] = PAINTING_PANELS[key];
      }
    }
    return result;
  }, [submitQAQCsTime]);

  return (
    <Row wrap={false} gutter={80}>
      <Col flex="310px" className="process-flow">
        <div className="process-flow-ttl">
          <span>Process Flow</span>
        </div>

        <ActionField name="validateData" request={{ name: 'validate' }}>
          {(actionProps: ActionFieldChildProps) => (
            <WorkFlow
              active={statusRequest?.status}
              data={paintingConfiguration}
              status={validateData(
                actionProps,
                paintingConfiguration,
                statusRequest?.status,
                { checkRole: auth.checkRole },
              )}
              getStatusName={status => {
                const header = MAP_REQUEST_NAME[status];
                const stage = status.match(/[\d]+/g)?.[0];
                if (
                  status.includes('PENDING_PAINTING_') &&
                  stage &&
                  parseInt(stage) > 0 &&
                  submitQAQCsTime.needToSubmittedTime > 1
                ) {
                  return `${
                    MAP_REQUEST_NAME['PENDING_PAINTING']
                  } (${getSuffixName(
                    stage,
                    submitQAQCsTime.needToSubmittedTime,
                  )} coat)`;
                }
                if (
                  status.includes('PENDING_QAQC_') &&
                  stage &&
                  parseInt(stage) > 0 &&
                  submitQAQCsTime.needToSubmittedTime > 1
                ) {
                  return `${MAP_REQUEST_NAME['PENDING_QAQC']} (${getSuffixName(
                    stage,
                    submitQAQCsTime.needToSubmittedTime,
                  )} coat)`;
                }
                return header;
              }}
              lastSubmitted={lastSubmittedStatus}
            />
          )}
        </ActionField>
      </Col>
      <Col flex="auto">
        <div className="create-insulation">
          <Row>
            <PageHeader
              left={
                <StatusTagColumnTable
                  value={getValueByStatus(
                    REQUEST_STATUS,
                    statusRequest?.status,
                  )}
                />
              }
              title={
                statusRequest?.id
                  ? statusRequest?.id
                  : 'Create Painting Request'
              }
              right={statusRequest?.id ? <ActivityLog /> : undefined}
            />
          </Row>
          {isSubmitSuccess.code === 1 && (
            <Row>
              <MessageSuccess
                message={isSubmitSuccess.message}
                type={MESSAGE_NOTICE.SUCCESS}
              />
            </Row>
          )}
          {isSubmitSuccess.code === 2 && (
            <Row>
              <MessageSuccess
                message="Request submit error: Information not filled completely"
                type={MESSAGE_NOTICE.ERROR}
              />
            </Row>
          )}

          <VisibleView config={paintingConfiguration} />
          <PaintingButtons
            statusRequest={statusRequest}
            onSetFormStatus={setFormStatus}
            formStatus={formStatus}
            onResetMessageNotice={setIsSubmitSuccess}
            listPanels={paintingConfiguration}
          />
        </div>
      </Col>
    </Row>
  );
});
export default Painting;
